import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import {
  Colors,
  DockedButton,
  Flex,
  GoodsCard,
  List,
  type ListRef,
  Spinner,
  StackScreen,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { AnalyticsScreen } from 'shared/src/context';
import { useHandleBackPressed } from 'shared/src/hooks/useHandleBackPressed/useHandleBackPressed';
import {
  useAndroidHardwareBackPressHandler,
  useAppDownloadModal,
  useIsAnonymousUser,
  useIsForegroundNow,
} from 'shared/src/hooks';
import { bridge, isNotNil, isWeb, useNavigator } from 'shared/src/utils';
import { systemAlert } from 'shared/src/component/systemAlert';
import type { FolderGoodsCardItem } from './utils/convertFolderGoodsItemToGoodsData';
import type { FolderDetailRow } from './types';
import {
  FOLDER_TYPE,
  SORT_TYPES,
  type DeliveryToggleType,
  type SortType,
} from './constants';
import { CategoryFilter } from './components/CategoryFilter';
import { CoverImage } from './components/CoverImage';
import { DeliveryFilter } from './components/DeliveryFilter';
import { EmptyView } from './components/EmptyView';
import { EditToggleButton } from './components/EditToggleButton';
import { FolderDetailScrollTopButton } from './components/FolderDetailScrollTopButton';
import { FolderShareButton } from './components/FolderShareButton';
import { SelectionCheckBox } from './components/SelectionCheckBox';
import { useCreateFolderBottomSheet } from '../Like/components/LikeFolder/hooks/useCreateFolderBottomSheet';
import { useCoverEditor } from './hooks/useCoverEditor';
import { useFolderPickerBottomSheet } from './hooks/useFolderPickerBottomSheet';
import { useLogApplyCategoryChipFilter } from './hooks/useLogApplyCategoryChipFilter';
import { useLogApplySort } from './hooks/useLogApplySort';
import { useLogClickDeliveryToggle } from './hooks/useLogClickDeliveryToggle';
import { useLogClickEditOff } from './hooks/useLogClickEditOff';
import { useLogClickEditOn } from './hooks/useLogClickEditOn';
import { useLogClickGoods } from './hooks/useLogClickGoods';
import { useLogClickMoveGoodsToOtherFolder } from './hooks/useLogClickMoveGoodsToOtherFolder';
import { useLogClickShareFolder } from './hooks/useLogClickShareFolder';
import { useLogClickSort } from './hooks/useLogClickSort';
import { useLogCompleteEditFolderCoverImage } from './hooks/useLogCompleteEditFolderCoverImage';
import { useLogCompleteMoveGoods } from './hooks/useLogCompleteMoveGoods';
import { useLogCompleteRemoveGoods } from './hooks/useLogCompleteRemoveGoods';
import { useLogDisplayGoods } from './hooks/useLogDisplayGoods';
import { useLogRefreshScreen } from './hooks/useLogRefreshScreen';
import { useLogViewFolder } from './hooks/useLogViewFolder';
import { useSortBottomSheet } from './hooks/useSortBottomSheet';
import { useBulkRemoveLikedGoods } from './queries/useBulkRemoveLikedGoods';
import { useFolderDetail } from './queries/useFolderDetail';
import { useFolderDetailCategories } from './queries/useFolderDetailCategories';
import { useFolderDetailMeta } from './queries/useFolderDetailMeta';
import { useMoveLikedGoods } from './queries/useMoveLikedGoods';
import { useLikedFolders } from '../Like/queries/useLikedFolders';
import { queryKey } from '../../constant/queryKey';

const SCREEN_NAME = 'FOLDER';

const COLUMN_COUNT = 3;
const COLUMN_GAP = 2;

// xlarge 버튼(56) + 상하 패딩(12*2) + 보더(1) = 81
const BOTTOM_BAR_HEIGHT = 81;

const SCROLL_TOP_THRESHOLD = 300;

const VIEWABILITY_CONFIG = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 500,
};

type FolderDetailParams = {
  folderSno?: number;
};

interface FolderDetailScreenProps {
  route?: { params?: FolderDetailParams };
}

export const FolderDetailScreen = ({ route }: FolderDetailScreenProps) => {
  // 웹은 라우트 파라미터가 문자열로 들어와 folderSno === 0(기본 서랍) 비교가 깨지므로 숫자로 강제한다.
  const folderSno = Number(route?.params?.folderSno ?? 0);
  return (
    <AnalyticsScreen screen={SCREEN_NAME}>
      <FolderDetailContent folderSno={folderSno} />
    </AnalyticsScreen>
  );
};

interface FolderDetailContentProps {
  folderSno: number;
}

const FolderDetailContent = ({ folderSno }: FolderDetailContentProps) => {
  const { showBackButton, onPressBack } = useHandleBackPressed();
  const { navigator } = useNavigator();
  const { width: screenWidth } = useScreenSize();
  const { show: showSortBottomSheet } = useSortBottomSheet();
  const { show: showFolderPicker } = useFolderPickerBottomSheet();
  const { show: showCreateFolder } = useCreateFolderBottomSheet();
  const isAnonymous = useIsAnonymousUser();
  const queryClient = useQueryClient();
  const isForegroundNow = useIsForegroundNow();

  const { data: allFolders } = useLikedFolders();
  const { mutateAsync: removeGoods } = useBulkRemoveLikedGoods();
  const { mutateAsync: moveGoods } = useMoveLikedGoods();

  const [selectedSort, setSelectedSort] = useState<SortType>(SORT_TYPES[0]);
  const [selectedCategorySno, setSelectedCategorySno] = useState<number | null>(null);
  const [isDeliveryFilterOn, setIsDeliveryFilterOn] = useState(false);
  const [deliveryToggleType, setDeliveryToggleType] = useState<DeliveryToggleType>('standard');

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [selectedGoodsSnos, setSelectedGoodsSnos] = useState<Set<number>>(new Set());
  const [isEditMode, setIsEditMode] = useState(false);
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;

  const { bottom: safeAreaBottom } = useSafeArea();
  const listRef = useRef<ListRef<FolderDetailRow>>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const bottomBarHeight = BOTTOM_BAR_HEIGHT + safeAreaBottom;

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const next = event.nativeEvent.contentOffset.y > SCROLL_TOP_THRESHOLD;
      setShowScrollTop(prev => (prev === next ? prev : next));
    },
    [],
  );

  const handleScrollToTop = useCallback(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);

  const { data: meta, refetch: refetchMeta } = useFolderDetailMeta({ folderSno });
  const { data: categories, refetch: refetchCategories } = useFolderDetailCategories({ folderSno });

  const isDefaultFolder = folderSno === 0;
  const folderType = isDefaultFolder ? FOLDER_TYPE.DEFAULT : FOLDER_TYPE.CUSTOM;

  useLogViewFolder({
    folderSno,
    folderType,
    folderName: meta?.title,
    likesCount: meta?.folder_likes_count,
  });

  const {
    data: detail,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchDetail,
    isLoading: isDetailLoading,
  } = useFolderDetail({
    folderSno,
    order: selectedSort.key,
    categorySno: selectedCategorySno ?? undefined,
    deliveryToggleType: isDeliveryFilterOn ? deliveryToggleType : undefined,
  });

  useEffect(() => {
    if (isDeliveryFilterOn) return;
    if (detail?.deliveryToggleType == null) return;
    setDeliveryToggleType(detail.deliveryToggleType);
  }, [detail?.deliveryToggleType, isDeliveryFilterOn]);

  // 다른 화면에서 찜한 상품이 반영되지 않으므로 포그라운드 (재)진입 시 무효화
  const isDetailLoadingRef = useRef(isDetailLoading);
  isDetailLoadingRef.current = isDetailLoading;
  useEffect(() => {
    if (!isForegroundNow || isDetailLoadingRef.current) return;
    queryClient.invalidateQueries({ queryKey: queryKey.folderDetailAll });
    queryClient.invalidateQueries({ queryKey: queryKey.folderDetailMetaAll });
    queryClient.invalidateQueries({
      queryKey: queryKey.folderDetailCategories(folderSno),
    });
  }, [isForegroundNow, folderSno, queryClient]);

  const isEmpty = !isDetailLoading && (detail?.goods.length ?? 0) === 0;
  const isFolderEmpty = (meta?.goods_count ?? 0) === 0;

  const showEditButton = !isAnonymous && !isFolderEmpty;
  const disableEditButton = isDefaultFolder && (meta?.goods_count ?? 0) < 1;

  const listData = useMemo<FolderDetailRow[]>(() => {
    const items: FolderDetailRow[] = [];
    if (!isDefaultFolder) items.push({ type: 'cover' });
    if (!isFolderEmpty) {
      items.push({ type: 'category' });
      items.push({ type: 'delivery' });
    }
    if (isEmpty) {
      items.push({ type: 'empty' });
      return items;
    }
    const goodsList = detail?.goods ?? [];
    for (let i = 0; i < goodsList.length; i += COLUMN_COUNT) {
      const rowItems = goodsList.slice(i, i + COLUMN_COUNT);
      items.push({
        type: 'goodsRow',
        key: `row-${rowItems[0]?.sno ?? i}`,
        items: rowItems,
      });
    }
    return items;
  }, [detail?.goods, isDefaultFolder, isEmpty, isFolderEmpty]);

  const stickyIndex = useMemo(() => {
    const index = listData.findIndex(row => row.type === 'category');
    return index >= 0 ? [index] : undefined;
  }, [listData]);

  const goodsCardWidth = useMemo(
    () => Math.floor((screenWidth - COLUMN_GAP * (COLUMN_COUNT - 1)) / COLUMN_COUNT),
    [screenWidth],
  );

  const logRefreshScreen = useLogRefreshScreen();
  const logClickDeliveryToggle = useLogClickDeliveryToggle({
    deliveryToggleType,
    folderType,
    folderSno,
    listId: detail?.listId ?? null,
    sortType: selectedSort.key,
    category:
      categories?.find(category => category.sno === selectedCategorySno) ?? null,
  });

  const handleChangeDeliveryFilter = useCallback(
    (isOn: boolean) => {
      logClickDeliveryToggle(isOn);
      setIsDeliveryFilterOn(isOn);
    },
    [logClickDeliveryToggle],
  );

  const handleRefresh = useCallback(async () => {
    logRefreshScreen();
    setIsRefreshing(true);
    try {
      await Promise.all([refetchMeta(), refetchCategories(), refetchDetail()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [logRefreshScreen, refetchMeta, refetchCategories, refetchDetail]);

  const logClickSort = useLogClickSort({
    sortType: selectedSort.key,
    folderType,
  });
  const logApplySort = useLogApplySort(folderType);

  const handlePressSort = useCallback(async () => {
    logClickSort();
    const result = await showSortBottomSheet({
      options: SORT_TYPES,
      selectedKey: selectedSort.key,
    });
    if (result.type !== 'confirm') return;
    setSelectedSort(result.data.selected);
    const matched = categories?.find(
      category => category.sno === selectedCategorySno,
    );
    logApplySort(result.data.selected.key, matched);
  }, [
    categories,
    logApplySort,
    logClickSort,
    selectedCategorySno,
    selectedSort.key,
    showSortBottomSheet,
  ]);

  const logApplyCategoryChipFilter = useLogApplyCategoryChipFilter(folderType);

  const handlePressCategory = useCallback(
    (sno: number | null) => {
      setSelectedCategorySno(sno);
      const matched = categories?.find(category => category.sno === sno);
      logApplyCategoryChipFilter(matched);
    },
    [categories, logApplyCategoryChipFilter],
  );

  const logClickGoods = useLogClickGoods(folderType);

  const logClickShareFolder = useLogClickShareFolder();
  const { show: showAppDownloadModal } = useAppDownloadModal();

  const handlePressShare = useCallback(() => {
    logClickShareFolder({
      folderSno,
      folderName: meta?.title,
      goodsCount: meta?.goods_count,
      likesCount: meta?.folder_likes_count,
      hasCoverImage: meta?.cover_image != null,
    });
    // 공유 시트는 네이티브 전용(카카오/인스타 스토리 등)이라 웹에서는 앱 설치로 유도한다.
    if (isWeb) {
      showAppDownloadModal();
      return;
    }
    const shareImageUrl = meta?.cover_image ?? detail?.goods[0]?.image;
    bridge.openAblyShareSheet({
      data: {
        type: 'FOLDER',
        url: meta?.web_view_url ?? '',
        folder_title: meta?.title ?? '',
        instagram_story_image_url: shareImageUrl ?? '',
        user_profile_image_url: meta?.owner?.profile_image ?? '',
        user_name: meta?.owner?.name ?? '',
      },
      logging: {
        FOLDER_SNO: folderSno.toString(),
        FOLDER_NAME: meta?.title,
        GOODS_COUNT: meta?.goods_count,
        IS_COVER_IMAGE_EXIST: meta?.cover_image ? '1' : '0',
        LIKES_COUNT: meta?.folder_likes_count,
        FAVORITES_COUNT: meta?.folder_favorites_count,
      },
    });
  }, [folderSno, meta, detail, logClickShareFolder, showAppDownloadModal]);

  const coverEditor = useCoverEditor({
    folderSno,
    title: meta?.title,
    remoteCoverImage: meta?.cover_image,
  });
  const logCompleteEditFolderCoverImage = useLogCompleteEditFolderCoverImage();

  const logClickEditOn = useLogClickEditOn(folderType);
  const logClickEditOff = useLogClickEditOff(folderType);

  const handleToggleEditMode = useCallback(async () => {
    if (!isEditMode) {
      logClickEditOn();
      setIsEditMode(true);
      return;
    }
    if (coverEditor.isCommitting) return;
    if (coverEditor.hasUnsavedChanges) {
      try {
        await coverEditor.commit();
        logCompleteEditFolderCoverImage({
          sno: folderSno,
          name: meta?.title,
          likesCount: meta?.folder_likes_count,
          favoritesCount: meta?.folder_favorites_count,
        });
      } catch {
        bridge.toast('이미지 업로드에 실패했어요. 잠시 후 다시 시도해주세요');
        return;
      }
    }
    setSelectedGoodsSnos(new Set());
    logClickEditOff();
    setIsEditMode(false);
  }, [
    coverEditor,
    folderSno,
    isEditMode,
    logClickEditOff,
    logClickEditOn,
    logCompleteEditFolderCoverImage,
    meta,
  ]);

  const handleBackPress = useCallback(() => {
    if (!coverEditor.hasUnsavedChanges) {
      onPressBack();
      return;
    }
    systemAlert('', '창을 닫으면 편집사항은 저장되지 않아요.\n정말 나갈까요?', [
      { text: '취소', style: 'cancel' },
      {
        text: '나가기',
        style: 'default',
        onPress: () => {
          coverEditor.reset();
          setIsEditMode(false);
          onPressBack();
        },
      },
    ]);
  }, [coverEditor, onPressBack]);

  useAndroidHardwareBackPressHandler(
    useCallback(() => {
      if (!coverEditor.hasUnsavedChanges) return false;
      handleBackPress();
      return true;
    }, [coverEditor.hasUnsavedChanges, handleBackPress]),
  );

  const handleToggleSelection = useCallback((sno: number) => {
    setSelectedGoodsSnos(prev => {
      const next = new Set(prev);
      if (next.has(sno)) {
        next.delete(sno);
      } else {
        next.add(sno);
      }
      return next;
    });
  }, []);

  const handlePressGoodsCard = useCallback((goods: FolderGoodsCardItem) => {
    if (isEditModeRef.current) {
      handleToggleSelection(goods.sno);
      return;
    }
    logClickGoods(goods);
    navigator.moveToGoods({
      sno: goods.sno,
      firstPageRendering: goods.first_page_rendering ?? null,
      remoteDeeplink: goods.remote_deeplink ?? undefined,
      loggingAnalytics: goods.logging.analytics,
    });
  }, [logClickGoods, navigator, handleToggleSelection]);

  const logCompleteRemoveGoods = useLogCompleteRemoveGoods(folderType);
  const logDisplayGoods = useLogDisplayGoods({
    resetKey: [
      selectedSort.key,
      selectedCategorySno ?? 'all',
      isDeliveryFilterOn ? 'on' : 'off',
    ].join('|'),
    folderType,
  });

  const handleConfirmDelete = useCallback(async () => {
    try {
      await removeGoods({
        like_snos: [...selectedGoodsSnos],
        target_folder_sno: folderSno,
      });
      logCompleteRemoveGoods();
      setSelectedGoodsSnos(new Set());
      bridge.toast('상품이 삭제되었습니다');
    } catch {
      bridge.toast('잠시 후 다시 시도해주세요');
    }
  }, [logCompleteRemoveGoods, removeGoods, selectedGoodsSnos, folderSno]);

  const handlePressDelete = useCallback(() => {
    if (selectedGoodsSnos.size === 0) return;
    systemAlert('상품 삭제', '선택하신 상품을 삭제하시겠어요?', [
      { text: '취소', style: 'cancel' },
      {
        text: '삭제',
        style: 'destructive',
        onPress: handleConfirmDelete,
      },
    ]);
  }, [selectedGoodsSnos.size, handleConfirmDelete]);

  const resolveMoveTarget = useCallback(
    async (pickerData: {
      isCreateNew: boolean;
      folder?: { sno: number; title: string };
    }): Promise<{ sno: number; name: string } | null> => {
      if (pickerData.isCreateNew) {
        const createResult = await showCreateFolder();
        if (createResult.type !== 'confirm') return null;
        return {
          sno: createResult.data.folderSno,
          name: createResult.data.folderName,
        };
      }
      if (pickerData.folder != null) {
        return {
          sno: pickerData.folder.sno,
          name: pickerData.folder.title,
        };
      }
      return null;
    },
    [showCreateFolder],
  );

  const logClickMoveGoodsToOtherFolder = useLogClickMoveGoodsToOtherFolder(folderType);
  const logCompleteMoveGoods = useLogCompleteMoveGoods(folderType);

  const handlePressMove = useCallback(async () => {
    if (selectedGoodsSnos.size === 0) return;
    if (allFolders == null) return;

    logClickMoveGoodsToOtherFolder();
    const pickerResult = await showFolderPicker({
      folders: allFolders,
      currentFolderSno: folderSno,
    });
    if (pickerResult.type !== 'confirm') return;

    const target = await resolveMoveTarget(pickerResult.data);
    if (target == null) return;

    try {
      await moveGoods({
        folder_sno: target.sno,
        like_snos: [...selectedGoodsSnos],
      });
      logCompleteMoveGoods();
      setSelectedGoodsSnos(new Set());
      setIsEditMode(false);
      bridge.toast(`상품이 '${target.name}' 으로 이동했어요`);
    } catch {
      bridge.toast('잠시 후 다시 시도해주세요');
    }
  }, [
    allFolders,
    folderSno,
    logClickMoveGoodsToOtherFolder,
    logCompleteMoveGoods,
    moveGoods,
    resolveMoveTarget,
    selectedGoodsSnos,
    showFolderPicker,
  ]);

  const hasSelection = selectedGoodsSnos.size > 0;
  const buttonState = hasSelection ? undefined : 'disabled';

  const bottomBar = isEditMode ? (
    <DockedButton
      stroke
      paired={'5:5'}
      color={'gray'}
      pairedButton={{
        title: '상품 삭제',
        onPress: handlePressDelete,
        state: buttonState,
      }}
      button={{
        title: '상품 이동',
        onPress: handlePressMove,
        state: buttonState,
      }}
    />
  ) : !isDefaultFolder && isNotNil(meta?.web_view_url) ? (
    <FolderShareButton
      isEnabled={(detail?.totalCount ?? 0) > 0}
      onPressShare={handlePressShare}
    />
  ) : null;

  const hasBottomBar = bottomBar != null;

  return (
    <Flex flex={1}>
      <StackScreen
        title={meta?.title ?? ''}
        hideBackButton={!showBackButton}
        onPressBack={handleBackPress}
        rightIcons={
          showEditButton
            ? [
                <EditToggleButton
                  disabled={disableEditButton}
                  onPress={handleToggleEditMode}
                >
                  {isEditMode ? '완료' : '편집'}
                </EditToggleButton>,
              ]
            : undefined
        }
        bottom={hasBottomBar ? { element: bottomBar } : undefined}
      >
        <List<FolderDetailRow>
          ref={listRef}
          data={listData}
          extraData={isEditMode}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          keyExtractor={row => (row.type === 'goodsRow' ? row.key : row.type)}
          stickyHeaderIndices={stickyIndex}
          showsVerticalScrollIndicator={false}
          viewabilityConfig={VIEWABILITY_CONFIG}
          onViewableItemsChanged={logDisplayGoods}
          renderItem={({ item: row }) => {
            switch (row.type) {
              case 'cover':
                return (
                  <CoverImage
                    coverImage={coverEditor.previewUri}
                    isEditMode={isEditMode}
                    onPress={coverEditor.promptAttach}
                  />
                );
              case 'empty':
                return <EmptyView isDeliveryFilterOn={isDeliveryFilterOn} />;
              case 'category':
                return (
                  <CategoryFilter
                    selectedSort={selectedSort}
                    categories={categories ?? []}
                    selectedCategorySno={selectedCategorySno}
                    isEditMode={isEditMode}
                    onPressSort={handlePressSort}
                    onPressCategory={handlePressCategory}
                  />
                );
              case 'delivery':
                return (
                  <DeliveryFilter
                    totalCount={detail?.totalCount ?? 0}
                    deliveryToggleType={isEditMode ? 'standard' : deliveryToggleType}
                    isDeliveryFilterOn={isDeliveryFilterOn}
                    onChangeDeliveryFilter={handleChangeDeliveryFilter}
                  />
                );
              case 'goodsRow':
                return (
                  <Flex.Row gap={COLUMN_GAP}>
                    {row.items.map(goods => (
                      <Flex key={goods.sno} flex={1}>
                        <GoodsCard
                          data={goods}
                          size={'medium'}
                          defaultWidth={goodsCardWidth}
                          onPress={() => handlePressGoodsCard(goods)}
                          onPressLike={async () => {}}
                        />
                        {isEditMode && (
                          <SelectionCheckBox
                            isSelected={selectedGoodsSnos.has(goods.sno)}
                          />
                        )}
                      </Flex>
                    ))}
                    {Array.from({ length: COLUMN_COUNT - row.items.length }).map((_, i) => (
                      <Flex key={`pad-${i}`} flex={1} />
                    ))}
                  </Flex.Row>
                );
            }
          }}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          onEndReachedThreshold={1}
          refreshing={isFolderEmpty ? undefined : isRefreshing}
          onRefresh={isFolderEmpty ? undefined : handleRefresh}
        />
      </StackScreen>
      <FolderDetailScrollTopButton
        visible={showScrollTop}
        bottomOffset={hasBottomBar ? bottomBarHeight : undefined}
        onPress={handleScrollToTop}
      />
      {(isDetailLoading || isFetchingNextPage || coverEditor.isCommitting) && (
        <Flex.Center
          position={'absolute'}
          top={0}
          left={0}
          right={0}
          bottom={0}
          pointerEvents={coverEditor.isCommitting ? undefined : 'none'}
          backgroundColorValue={coverEditor.isCommitting ? Colors.overlay_dark : undefined}
        >
          <Spinner
            color={coverEditor.isCommitting ? 'content_always_white' : 'content_tertiary'}
            size={24}
          />
        </Flex.Center>
      )}
    </Flex>
  );
};
